import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Form } from 'react-bootstrap'
import { Col, Row } from 'reactstrap'
import { DatePicker } from 'antd'
import locale from 'antd/es/date-picker/locale/es_ES'
import moment from 'moment'

import { getVehicleTypes } from '../../VehicleTypes/services'
import { getOrders } from '../../Orders/services'
import { getAvailableTimeSlots, getUpcomingUnavailableDays } from '../services'
import { handleError } from '../../../helpers'

export default function AppointmentForm({ register, errors, watch, setValue, setError, clearErrors, defaultOrder = null }) {
    const user = useSelector(state => state.auth.user)
    const [orders, setOrders] = useState(null)
    const [vehicleTypes, setVehicleTypes] = useState(null)
    const [availableTimeSlots, setAvailableTimeSlots] = useState(null)
    const [selectedVehicleType, setSelectedVehicleType] = useState(null)
    const [selectedOrder, setSelectedOrder] = useState(defaultOrder)
    const [upcomingUnavailableDays, setUpcomingUnavailableDays] = useState(null)
    const [upcomingUnavailableDaysList, setUpcomingUnavailableDaysList] = useState([])
    const targetDate = watch('target_date')

    useEffect(() => {
        !vehicleTypes && getVehicleTypes({ 'filter[active]': 1, sort: 'name'})
            .then(res => setVehicleTypes(res.data.data))
            .catch(error => handleError(error))
    }, [vehicleTypes])

    useEffect(() => {
        !orders && getOrders({ 
            'filter[supplier_id]': user.id, 
            'filter[pending_appointment]': 1,
            include: 'activeOrderDetails',
            sort: '-registered_at', 
        })
            .then(res => setOrders(res.data.data))
            .catch(error => handleError(error))
    }, [orders, user])

    useEffect(() => {
        !upcomingUnavailableDays && getUpcomingUnavailableDays()
            .then(res => setUpcomingUnavailableDays(res.data.data))
            .catch(error => handleError(error))
        setUpcomingUnavailableDaysList(upcomingUnavailableDays?.map(day => day.date) || [])
    }, [upcomingUnavailableDays])

    useEffect(() => {
        if (selectedOrder && selectedVehicleType && targetDate) {
            getAvailableTimeSlots({ 
                scheduledDate: targetDate, 
                unloadTime: selectedVehicleType.unload_time, 
                warehouseSku: selectedOrder.location_sku 
            })
                .then(res => setAvailableTimeSlots(res.data.data))
                .catch(error => handleError(error))
        }
    }, [selectedOrder, selectedVehicleType, targetDate])

    const handleVehicleTypeChange = vehicleTypeId => {
        setValue('vehicle_type_id', vehicleTypeId)
        let vehicleType = vehicleTypes?.find(vehicleType => vehicleType.id === parseInt(vehicleTypeId))
        setSelectedVehicleType(vehicleType)
    }

    const handleOrderChange = orderId => {
        setValue('time_slot', '')
        setValue('target_date', '')
        setValue('order_detail_id', '')
        setValue('schedule_at', '')
        setAvailableTimeSlots(null)
        setValue('order_id', orderId)
        let order = orders?.find(order => order.id === parseInt(orderId))
        setSelectedOrder(order)
        setValue('warehouse_id', order ? order.warehouse_id : '')
        setValue('location_sku', order ? order.location_sku : '')
    }

    const handleOrderDetailChange = orderDetailId => {
        setValue('order_detail_id', orderDetailId)
        let orderDetail = selectedOrder?.active_order_details?.find(orderDetail => orderDetail.id === parseInt(orderDetailId))
        setValue('schedule_at', orderDetail ? moment(orderDetail.reception_planned_at).format('YYYY-MM-DD') : '')
    }

    const handleTimeSlotChange = (timeSlot, warehouseDockId) => {
        setValue('start_at', moment(targetDate + ' ' + timeSlot).format('YYYY-MM-DD HH:mm:ss'))
        setValue('end_at', moment(targetDate + ' ' + timeSlot).add(selectedVehicleType.unload_time, 'minutes').format('YYYY-MM-DD HH:mm:ss'))
        setValue('warehouse_dock_id', warehouseDockId)
        setValue('time_slot', timeSlot)
    }

    const validateDate = (value) => {
       let newDate = moment(value.format('YYYY-MM-DD'))

       if ( newDate.isSameOrAfter(moment().add(1, 'days').startOf('day')) &&
          newDate.isBefore(moment().add(60, 'days').startOf('day')) &&
          !upcomingUnavailableDaysList.includes(newDate.format('YYYY-MM-DD'))
       ) {
          setValue('target_date', newDate.format('YYYY-MM-DD'))
          clearErrors()
       } else {
          setError('target_date')
          setValue('target_date', '')
       }
    }

	return (
		<React.Fragment>
            <Form.Control {...register('order_id', { required:true })} style={{ display: 'none' }} />
            <Form.Control {...register('vehicle_type_id', { required:true })} style={{ display: 'none' }} />
            <Form.Control {...register('warehouse_id', { required:true })} style={{ display: 'none' }} />
            <Form.Control {...register('warehouse_dock_id', { required:true })} style={{ display: 'none' }} />
            <Form.Control {...register('start_at', { required:true })} style={{ display: 'none' }} />
            <Form.Control {...register('end_at', { required:true })} style={{ display: 'none' }} />
            <Form.Control {...register('time_slot', { required:true })} style={{ display: 'none' }} />
            <Form.Group className='form-group'>
                <Form.Label>Nombre de la persona que agenda <span className='text-primary'>*</span></Form.Label>
                <Form.Control
                    type="text"
                    placeholder='Escriba aquí...'
                    {...register('booked_by_name', { required:true })}
                />
                { errors.booked_by_name && <Form.Text className='text-warning'>Este campo es requerido</Form.Text> }
            </Form.Group>
            <Form.Group className='form-group'>
                <Form.Label>Correo para notificaciones <span className='text-primary'>*</span></Form.Label>
                <Form.Control
                    type="email"
                    placeholder='Escriba aquí...'
                    {...register('notification_email', { required:true })}
                    defaultValue={user.notification_email}
                />
                { errors.notification_email && <Form.Text className='text-warning'>Este campo es requerido</Form.Text> }
                <Form.Text className='text-muted'>Correo electrónico para notificaciones de cambios en la agenda.</Form.Text>
            </Form.Group>
            <Row className='gx-2'>
                <Form.Group className='form-group' as={Col}>
                    <Form.Label>Orden de compra <span className='text-primary'>*</span></Form.Label>
                    <Form.Control
                        as="select"
                        disabled={!orders}
                        value={selectedOrder ? selectedOrder.id : ''}
                        onChange={e => handleOrderChange(e.target.value)}
                    >
                        <option value="">:: Seleccione una opción ::</option>
                        { orders?.map(order => (
                            <option key={order.id} value={order.id}>
                                {order.sku}
                            </option>
                        )) }
                    </Form.Control>
                    { (errors.warehouse_id || errors.order_id) && <Form.Text className='text-warning'>Este campo es requerido</Form.Text> }
                </Form.Group>
                <Form.Group className='form-group' as={Col}>
                    <Form.Label>Código de bodega</Form.Label>
                    <Form.Control 
                        disabled 
                        value={selectedOrder ? selectedOrder.location_sku : 'Eliga una orden'} 
                    />
                </Form.Group>
            </Row>
            <Row className='gx-2'>
                <Form.Group className='form-group' as={Col}>
                    <Form.Label>Articulo Principal <span className='text-primary'>*</span></Form.Label>
                    <Form.Control
                        as="select"
                        disabled={!selectedOrder}
                        value={selectedOrder ? selectedOrder.active_order_details.id : ''}
                        onChange={e => handleOrderDetailChange(e.target.value)}
                    >
                        <option value="">:: Seleccione una opción ::</option>
                        { selectedOrder?.active_order_details?.map(orderDetail => (
                            <option key={orderDetail.id} value={orderDetail.id}>
                                {orderDetail.article_name} ({orderDetail.reception_planned_at})
                            </option>
                        )) }
                    </Form.Control>
                </Form.Group>
            </Row>
            <Row className='gx-2'>
                <Form.Group className='form-group' as={Col}>
                    <Form.Label>Recepción Planeada</Form.Label>
                    <Form.Control 
                        disabled 
                        placeholder='Eliga un articulo principal'
                        {...register('schedule_at', { required:true })}
                    />
                </Form.Group>
                <Form.Group as={Col} md="6">
                    <Form.Label>Fecha Agendamiento <span className='text-primary'>*</span></Form.Label>
                    <DatePicker
                        format="YYYY-MM-DD"
                        disabledDate={current => {
                            let result = false
                            if(current && selectedOrder){
                                if(current <= moment(watch('schedule_at'))) result = true
                                if(current > moment().add(60, 'days')) result = true
                                if(current.weekday() === 0) result = true
                                if(upcomingUnavailableDaysList.includes(current.format('YYYY-MM-DD'))) result = true
                            }
                            return result
                        }}
                        placeholder="Eliga una fecha"
                        className="full-width"
                        onChange={value => validateDate(value)}
                        locale={locale}
                        disabled={!selectedOrder}
                    />
                    <input
                        type="hidden"
                        {...register('target_date', { required: true })}
                    />
                    {errors.target_date && <Form.Text className="text-warning">Esta fecha no es válida</Form.Text>}
                </Form.Group>
            </Row>
            <Row className='gx-2'>
                <Form.Group className='form-group' as={Col}>
                    <Form.Label>Tipo de Vehículo <span className='text-primary'>*</span></Form.Label>
                    <Form.Control
                        as="select"
                        value={selectedVehicleType ? selectedVehicleType.id : ''}
                        onChange={e => handleVehicleTypeChange(e.target.value)}
                        disabled={!vehicleTypes}
                    >
                        <option value="">:: Seleccione una opción ::</option>
                        { vehicleTypes?.map(vehicleType => (
                            <option key={vehicleType.id} value={vehicleType.id}>
                                {vehicleType.name}
                            </option>
                        )) }
                    </Form.Control>
                    { errors.vehicle_type_id && <Form.Text className='text-warning'>Este campo es requerido</Form.Text> }
                </Form.Group>
                <Form.Group className='form-group' as={Col} sm={5}>
                    <Form.Label>Tiempo de descargue</Form.Label>
                    <Form.Control disabled value={selectedVehicleType ? `${selectedVehicleType.unload_time} minutos` : 'Eliga un tipo de vehículo'} />
                </Form.Group>
            </Row>
            <Form.Group className='form-group'>
                <Form.Label>Horas disponibles <span className='text-primary'>*</span></Form.Label>
                { availableTimeSlots ? (
                    <>
                        { Object.keys(availableTimeSlots).length > 0 ? (
                            <div className='mt-10 mb-10'>
                                <div className='d-flex flex-wrap gap-2'>
                                    { Object.entries(availableTimeSlots).map(([timeSlot, warehouseDockId]) => (
                                        <div 
                                            key={timeSlot} 
                                            className={`btn ${watch('time_slot') === timeSlot ? 'btn-warning' : 'btn-outline-dark'} ${errors.warehouse_dock_id ? 'disabled' : ''}`} 
                                            onClick={() => handleTimeSlotChange(timeSlot, warehouseDockId)}
                                        >
                                            {timeSlot}
                                            <span className='small btn-text-span'>
                                                {" "}
                                            </span>
                                        </div>
                                    )) }
                                </div>
                            </div>
                        ) : (
                            <Form.Text className='block text-danger'><em>No hay horas disponibles para esta fecha</em></Form.Text>
                        ) }
                    </>
                ) : (
                    <Form.Text className='block text-muted'><em>Diligencia el resto del formulario primero</em></Form.Text>
                )}
                { errors.time_slot && <Form.Text className='text-warning'>Este campo es requerido</Form.Text> }
            </Form.Group>
		</React.Fragment>
	)
}